import { cleanText } from './ai-presentation';
import { aiComplete } from './ai-complete';
import type { AiTips } from './types';

// Gemini 2.5 Flash (server-side gepind in /ai/complete): fors sneller dan Sonnet 4
// voor deze analyse, conform tenant-modelkeuze. LET OP: bij Gemini 2.5 Flash tellen
// thinking-tokens mee in max_tokens, dus ruim zetten (8192) anders komt de JSON
// afgekapt of leeg terug.
const ANALYSIS_MAX_TOKENS = 8192;

interface AnalyzeInput {
  transcript: string;
  client_name?: string | null;
  budget?: number;
  quantity?: number;
  budget_type?: 'incl_btw' | 'excl_btw';
  deadline?: string | null;
}

export interface AnalyzeOutput {
  summary: string;
  tips: AiTips;
}

const SUPPLIERS = [
  'Bickery (groothandel met o.a. Buonissimo, Botanic Chic, Holland Foodz)',
  'The Gift (non-food gadgets, trolleys, accessoires)',
  'Rehafood (bonbons, koek, food)',
  'Van Dijk (food + Campenaer koffie)',
  'Zoet Moment (snoep, koekjes, chocolade)',
  'Delicious Food (specialiteiten, augurken, sausen)',
  'Zaailander (Friese speciaalbieren: Boekweit Blond, Alt, Keller, Klimroos, Maizon)',
  'Tastemakers (food)',
  'DS Smith (verpakkingsdozen: Cosy, Happiness, Big Surprise, Box of Joy)',
];

const SYSTEM_PROMPT = `Je bent een expert relatiegeschenken-adviseur bij 2BMade in Leeuwarden. Mark Tromp stelt pakketten samen op basis van klantgesprekken. Analyseer een gesprek en geef advies in puur JSON.

Beschikbare leveranciers in de catalogus:
${SUPPLIERS.map((s) => '- ' + s).join('\n')}

Output STRIKT als JSON object met exact deze keys:
{
  "summary": "5 tot 7 zinnen Nederlandstalige samenvatting van het gesprek, met focus op: occasion, sfeer, doel pakket, klant-achtergrond, expliciete wensen, budget-realisme. Lopende zinnen, GEEN opmaak: geen sterretjes, geen vetgedrukt (**), geen opsommingstekens, geen kopjes.",
  "tips": {
    "dietary_warnings": ["geen alcohol", "vegetarisch", "noten-allergie", ...],
    "supplier_tips": [
      { "supplier": "Zaailander", "reason": "Lokaal Fries past bij het regionale karakter dat klant noemt" }
    ],
    "product_suggestions": [
      { "name": "Buonissimo Cannoli", "supplier": "Bickery", "reason": "Italiaanse touch matched klant-thema" }
    ]
  }
}

Regels:
- Wees concreet en operationeel, niet generiek.
- Allergie/dieet alleen toevoegen als expliciet of impliciet genoemd.
- Suggesties max 4 leveranciers, max 5 producten.
- Geen markdown, geen uitleg buiten het JSON object.
- Schrijf in normaal Nederlands. Geen woorden als 'leverage', 'robust', 'seamless'. Geen em-dashes.`;

export async function analyzeConversation(input: AnalyzeInput): Promise<AnalyzeOutput> {
  const userMessage = [
    input.client_name ? `Klant: ${input.client_name}` : '',
    input.quantity && input.budget
      ? `Bestelling: ${input.quantity} pakketten, EUR ${input.budget} ${input.budget_type === 'incl_btw' ? 'incl' : 'excl'} btw per pakket`
      : '',
    input.deadline ? `Deadline: ${input.deadline}` : '',
    '',
    'Gesprek:',
    input.transcript,
  ]
    .filter(Boolean)
    .join('\n');

  const content = await aiComplete({
    messages: [
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: userMessage },
    ],
    json: true,
    temperature: 0.4,
    max_tokens: ANALYSIS_MAX_TOKENS,
  });

  const parsed = parseAiJson(content);
  return {
    summary: parsed.summary ? cleanText(parsed.summary) : '',
    tips: {
      dietary_warnings: parsed.tips?.dietary_warnings ?? [],
      supplier_tips: parsed.tips?.supplier_tips ?? [],
      product_suggestions: parsed.tips?.product_suggestions ?? [],
    },
  };
}

// Claude (via OpenRouter) wikkelt JSON soms in markdown fences ondanks
// response_format=json_object. Probeer in volgorde: direct, fence-stripped,
// of substring tussen eerste { en laatste }.
function parseAiJson(raw: string): {
  summary?: string;
  tips?: {
    dietary_warnings?: string[];
    supplier_tips?: { supplier: string; reason: string }[];
    product_suggestions?: { name: string; supplier?: string; reason: string }[];
  };
} {
  const attempts: string[] = [raw.trim()];

  const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fenceMatch?.[1]) attempts.push(fenceMatch[1].trim());

  const first = raw.indexOf('{');
  const last = raw.lastIndexOf('}');
  if (first >= 0 && last > first) attempts.push(raw.slice(first, last + 1));

  for (const candidate of attempts) {
    try {
      return JSON.parse(candidate);
    } catch {
      // probeer volgende
    }
  }

  const snippet = raw.slice(0, 120).replace(/\s+/g, ' ');
  throw new Error(`AI-response niet als JSON parsebaar (start: "${snippet}...")`);
}

export async function uploadAudioFile(file: File): Promise<{ path: string; url: string }> {
  const { supabase } = await import('./supabase');
  const ext = file.name.split('.').pop() ?? 'mp3';
  const path = `pakket-app/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from('transcriptions').upload(path, file, {
    contentType: file.type || 'audio/mpeg',
  });
  if (error) throw error;
  const { data } = supabase.storage.from('transcriptions').getPublicUrl(path);
  return { path, url: data.publicUrl };
}
